import { Link } from "react-router-dom";
import { articles, featuredArticles, hotArticles } from "@/mocks/articles";
import { usePageMeta } from "@/hooks/usePageMeta";
import ArticleCard from "@/components/feature/ArticleCard";
import SectionHeading from "@/components/feature/SectionHeading";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import HotRanking from "@/pages/home/components/HotRanking";

const sortedByDate = (items: typeof articles) =>
  [...items].sort((a, b) => (a.date < b.date ? 1 : -1));

const latestGossip = sortedByDate(articles.filter((item) => item.category === "gossip")).slice(0, 6);
const deepBlack = sortedByDate(articles.filter((item) => item.category === "black")).slice(0, 3);
const newsList = sortedByDate(articles.filter((item) => item.category === "news")).slice(0, 4);
const tickerItems = sortedByDate(articles).slice(0, 4);

export default function Home() {
  usePageMeta({
    title: "黑料吃瓜—最新娱乐八卦新闻与明星黑料揭秘",
    description:
      "黑料吃瓜网站专注于发布最新的娱乐八卦新闻和明星黑料，涵盖热门事件和深度解析，帮助用户快速了解娱乐圈动态和吃瓜信息。",
    keywords: "黑料吃瓜,娱乐八卦,明星黑料,娱乐新闻,娱乐动态,八卦爆料,娱乐资讯,最新黑料",
  });

  return (
    <div className="w-full">
      <h1 className="sr-only">黑料吃瓜 - 最新娱乐八卦新闻与明星黑料揭秘</h1>
      <div className="max-w-[1320px] mx-auto px-4 md:px-6 pt-4 md:pt-6">
        <div className="flex items-center gap-3 rounded-md border border-background-200 bg-background-100 px-3.5 py-2 mb-4">
          <span className="shrink-0 inline-flex items-center gap-1 text-[11.5px] font-bold text-accent-700 bg-accent-100 rounded px-2 py-0.5 whitespace-nowrap">
            <i className="ri-flashlight-fill" />
            今日速报
          </span>
          <div className="flex-1 min-w-0 overflow-hidden">
            <div className="flex items-center gap-6 md:gap-10 overflow-x-auto no-scrollbar">
              {tickerItems.map((item) => (
                <Link
                  key={item.id}
                  to={`/detail/${item.id}`}
                  className="text-[12.5px] text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {item.title}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_336px] gap-4 md:gap-5">
          <HeroCarousel slides={featuredArticles} />

          <div className="flex flex-col gap-4">
            <HotRanking articles={hotArticles.slice(0, 5)} />
            <div className="rounded-lg border border-background-200 bg-accent-50 px-4 py-3.5">
              <h3 className="font-heading text-[15px] font-bold text-primary-900 flex items-center gap-2">
                <i className="ri-megaphone-line text-accent-600" />
                有料想爆？
              </h3>
              <p className="text-[12.5px] text-foreground-600 leading-6 mt-1.5">
                匿名投递线索，编辑会在核实后跟进。我们只做事实核对，不参与传闻加工。
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-1 mt-3 text-[12.5px] font-medium text-accent-700 hover:text-accent-800 cursor-pointer"
              >
                前往线索投递
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1320px] mx-auto px-4 md:px-6 mt-8 md:mt-11">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_336px] gap-6 md:gap-8">
          <section>
            <SectionHeading
              title="最新八卦动态"
              desc="按发布时间倒序，追踪明星恋情、绯闻与公开回应"
              moreTo="/gossip"
              moreLabel="进入八卦频道"
            />
            <div className="flex flex-col gap-3">
              {latestGossip.map((article) => (
                <ArticleCard key={article.id} article={article} variant="list" />
              ))}
            </div>
          </section>

          <aside className="flex flex-col gap-5">
            <div>
              <SectionHeading title="热点事件追踪" accent="primary" />
              <div className="rounded-lg border border-background-200 bg-background-50 divide-y divide-background-200">
                {hotArticles.slice(0, 3).map((article) => (
                  <Link
                    key={article.id}
                    to={`/detail/${article.id}`}
                    className="block px-4 py-3 group cursor-pointer hover:bg-background-100 transition-colors"
                  >
                    <span className="text-[10.5px] font-medium text-accent-700 bg-accent-50 border border-accent-200 rounded px-1.5 py-0.5">
                      {article.tags[0]}
                    </span>
                    <h4 className="text-[13.5px] font-medium text-foreground-800 leading-6 mt-1.5 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h4>
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-background-200 overflow-hidden">
              <div className="w-full h-36 bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Editorial%20newsroom%20desk%20at%20dusk%20with%20laptop%2C%20newspapers%2C%20coffee%20cup%20and%20warm%20desk%20lamp%2C%20cinematic%20top%20down%20composition%2C%20deep%20navy%20and%20amber%20tones%2C%20no%20people%2C%20no%20readable%20text&width=700&height=420&seq=home-editorial-desk&orientation=landscape"
                  alt="黑料吃瓜编辑部的选题工作台"
                  title="黑料吃瓜编辑部 - 娱乐资讯编辑工作台"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-4">
                <h3 className="font-heading text-[15px] font-bold text-foreground-900">编辑部在选择什么</h3>
                <p className="text-[12.5px] text-foreground-500 leading-6 mt-1.5">
                  我们优先处理能交叉验证的线索：公开行程、合同条款、多方回应。无法核实的传闻会标注为「讨论中」。
                </p>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-1 mt-3 text-[12.5px] text-primary-600 hover:text-primary-700 cursor-pointer"
                >
                  了解我们的准则
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="mt-9 md:mt-12 py-8 md:py-10 bg-background-100 border-y border-background-200">
        <div className="max-w-[1320px] mx-auto px-4 md:px-6">
          <SectionHeading
            title="深度黑料"
            desc="从公开资料与多方采访中还原事件全貌"
            moreTo="/black"
            moreLabel="进入黑料频道"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {deepBlack.map((article) => (
              <ArticleCard key={article.id} article={article} variant="feature" />
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-[1320px] mx-auto px-4 md:px-6 mt-9 md:mt-12">
        <SectionHeading
          title="娱乐新闻"
          desc="电影、剧集、综艺与演出市场的行业观察"
          moreTo="/news"
          moreLabel="进入新闻频道"
          accent="primary"
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8">
          {newsList.map((article) => (
            <Link
              key={article.id}
              to={`/detail/${article.id}`}
              className="group flex gap-4 py-4 border-b border-background-200 cursor-pointer"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-[11.5px] text-foreground-400">
                  <span className="text-primary-700 font-medium">{article.newsType}</span>
                  <span>{article.date}</span>
                </div>
                <h3 className="font-heading text-[15.5px] font-bold text-foreground-900 leading-snug mt-1 line-clamp-2 group-hover:text-primary-600 transition-colors">
                  {article.title}
                </h3>
                <p className="text-[12.5px] text-foreground-500 leading-6 mt-1.5 line-clamp-2">{article.excerpt}</p>
              </div>
              <div className="w-24 h-20 md:w-32 md:h-24 shrink-0 rounded-md overflow-hidden bg-background-200">
                <img
                  src={article.cover}
                  alt={article.title}
                  title={`${article.title} - 黑料吃瓜`}
                  className="w-full h-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-500"
                />
              </div>
            </Link>
          ))}
        </div>
      </div>

      <section className="max-w-[1320px] mx-auto px-4 md:px-6 mt-10 md:mt-14">
        <div className="rounded-lg bg-primary-950 text-background-100 px-5 md:px-10 py-8 md:py-10 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto] items-center gap-6">
          <div>
            <p className="text-[11.5px] tracking-[0.24em] text-accent-400 mb-2">ABOUT HEILIAO</p>
            <h2 className="font-heading text-xl md:text-[26px] font-black text-background-50 leading-snug">
              吃瓜也要吃明白：一个只讲时间线的娱乐资讯站
            </h2>
            <p className="text-[13px] md:text-sm text-background-300 leading-7 mt-3 max-w-2xl">
              黑料吃瓜不做标题党式的情绪加工。每条爆料都会标注消息来源、时间节点与当事方回应，无法确认的部分会明确写出「尚未证实」。我们希望你在吃完这个瓜之后，能清楚知道哪些是事实、哪些只是讨论。
            </p>
          </div>
          <div className="flex gap-6 lg:gap-8">
            {[
              { value: "3,200+", label: "累计爆料条目" },
              { value: "18", label: "签约内容编辑" },
              { value: "24h", label: "热点跟进速度" },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="font-heading text-xl md:text-2xl font-black text-accent-400">{stat.value}</p>
                <p className="text-[11.5px] text-background-300/80 mt-1 whitespace-nowrap">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}